import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Slider,
  Stack,
  Typography,
} from '@mui/material';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ReplayIcon from '@mui/icons-material/Replay';
import HillChart from './HillChart';
import { shortDate } from '../../utils/relativeTime';

const DAY = 86400000;
// Slow enough to watch, quick enough to sit through a six week cycle.
const FRAME_MS = 450;

/**
 * The hill as it stood on a chosen day, scrubbable across the cycle.
 *
 * A finished cycle looks tidy in hindsight; watching it unfold shows where the
 * week went. Dots that hung near the top for a fortnight are the ones worth
 * talking about in a retrospective, and they are invisible in a final snapshot.
 */
const HillReplay = ({ scopes = [], history = null, onClose }) => {
  const days = useMemo(() => buildTimeline(history), [history]);
  const [index, setIndex] = useState(Math.max(0, days.length - 1));
  const [playing, setPlaying] = useState(false);
  const timer = useRef(null);

  // A longer history arriving after a sync should not leave the handle stranded.
  useEffect(() => {
    setIndex(Math.max(0, days.length - 1));
  }, [days.length]);

  useEffect(() => {
    if (!playing) return undefined;

    timer.current = setInterval(() => {
      setIndex(current => {
        if (current >= days.length - 1) {
          setPlaying(false);
          return current;
        }
        return current + 1;
      });
    }, FRAME_MS);

    return () => clearInterval(timer.current);
  }, [playing, days.length]);

  const trails = useMemo(() => trailsUpTo(history, days[index]), [history, days, index]);
  const atDay = days[index];
  const replayed = useMemo(() => positionsAt(scopes, history, atDay), [scopes, history, atDay]);
  const notes = useMemo(() => notesOn(history, atDay), [history, atDay]);

  if (!history || days.length < 2) {
    return (
      <Box sx={{ py: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          There is only one day of history so far. Come back once the dots have had
          a chance to move.
        </Typography>
      </Box>
    );
  }

  const isLastDay = index === days.length - 1;

  const play = () => {
    // Pressing play at the end should start over rather than do nothing.
    if (isLastDay) setIndex(0);
    setPlaying(true);
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <IconButton
          size="small"
          onClick={playing ? () => setPlaying(false) : play}
          aria-label={playing ? 'Pause replay' : 'Play replay'}
        >
          {playing ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
        </IconButton>
        <Chip size="small" label={shortDate(atDay)} sx={{ fontWeight: 700, minWidth: 76 }} />
        <Slider
          size="small"
          min={0}
          max={days.length - 1}
          value={index}
          onChange={(_, value) => { setPlaying(false); setIndex(value); }}
          aria-label="Day of the cycle"
          sx={{ mx: 1 }}
        />
        <Typography variant="caption" color="text.disabled" sx={{ whiteSpace: 'nowrap' }}>
          {replayed.length} of {scopes.length} scopes
        </Typography>
        {onClose && (
          <IconButton size="small" onClick={onClose} aria-label="Close replay">
            <ReplayIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>

      <HillChart scopes={replayed} trails={trails} showTrails readOnly />

      {/* What people said on the day they moved a dot. The positions show that a
          scope stalled; these are the only record of what it stalled on. */}
      {notes.length > 0 && (
        <Stack spacing={0.5} sx={{ mt: 1, mb: 1 }}>
          {notes.map((entry, position) => (
            <Typography key={`${entry.scopeId}-${position}`} variant="caption" color="text.secondary">
              <strong>{entry.jiraKey}</strong> · {entry.note}
            </Typography>
          ))}
        </Stack>
      )}

      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center' }}>
        {isLastDay
          ? 'Today. Drag back to watch the cycle unfold.'
          : `As it stood on ${shortDate(atDay)}. Scopes that did not exist yet are absent.`}
      </Typography>
    </Box>
  );
};

/**
 * One entry per day from the first thing that happened through today.
 *
 * The cycle's own start is preferred where it exists, since a cycle that began
 * before anyone synced should still replay from its beginning.
 */
function buildTimeline(history) {
  if (!history?.scopes?.length) return [];

  const stamps = history.scopes
    .flatMap(scope => scope.trail.map(point => new Date(point.capturedAt).getTime()))
    .filter(Number.isFinite);

  if (!stamps.length) return [];

  const cycleStart = history.cycleStart ? new Date(history.cycleStart).getTime() : null;
  const first = startOfDay(Math.min(...stamps, cycleStart || Infinity));
  const last = startOfDay(Math.max(...stamps, Date.now()));

  const days = [];
  for (let day = first; day <= last; day += DAY) {
    days.push(new Date(day));
  }
  return days;
}

// Days are compared at local midnight so a slider step means the same thing
// regardless of what time of day a sync happened to run.
function startOfDay(value) {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

/**
 * Each scope as it stood at the end of a given day. A scope with no recorded
 * position by then did not exist yet and is left off the hill entirely, rather
 * than being drawn at the start line as though it were waiting.
 */
function positionsAt(scopes, history, at) {
  if (!history || !at) return scopes;

  const byId = new Map((history.scopes || []).map(entry => [entry.id, entry.trail || []]));
  const cutoff = startOfDay(at) + DAY - 1;

  return scopes.reduce((acc, scope) => {
    const trail = byId.get(scope.id) || [];
    let point = null;
    for (const candidate of trail) {
      if (new Date(candidate.capturedAt).getTime() <= cutoff) point = candidate;
      else break;
    }
    if (!point) return acc;

    acc.push({
      ...scope,
      position: point.position,
      positionSource: point.positionSource,
      jiraStatus: point.jiraStatus,
      jiraStatusCategory: point.jiraStatusCategory,
      // What was said at the time, rather than the latest word on it.
      lastJudgmentNote: point.note || null,
      // Whether a status was mapped back then is not recorded, so no dot in a
      // replay claims to be a guess; the source alone distinguishes them.
      isProvisional: false,
      // Staleness is a live concern; in a replay every dot is by definition old.
      isStuck: false,
      stillForDays: null,
    });
    return acc;
  }, []);
}

/**
 * Whatever anyone wrote down on the day being viewed.
 */
function notesOn(history, at) {
  if (!history || !at) return [];

  const from = startOfDay(at);
  const to = from + DAY - 1;

  return (history.scopes || []).flatMap(entry => (entry.trail || [])
    .filter((point) => {
      if (!point.note) return false;
      const stamp = new Date(point.capturedAt).getTime();
      return stamp >= from && stamp <= to;
    })
    .map(point => ({ scopeId: entry.id, jiraKey: entry.jiraKey, note: point.note })));
}

/**
 * Breadcrumbs limited to the day being viewed, so scrubbing back also rewinds
 * the trail instead of revealing where a dot is going to go.
 */
function trailsUpTo(history, at) {
  if (!history || !at) return {};

  const cutoff = startOfDay(at) + DAY - 1;
  return (history.scopes || []).reduce((acc, entry) => {
    acc[entry.id] = (entry.trail || []).filter(
      point => new Date(point.capturedAt).getTime() <= cutoff
    );
    return acc;
  }, {});
}

export default HillReplay;
